import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Plus, Minus } from 'react-feather';
import { css, cx } from '@emotion/css';
import {
  hexToRgb,
  webkitScrollBarWeight,
  hasTransparency,
} from '../../utils';

const wrapper = (params) => css`
  display: flex;
  flex-direction: column;
  margin: 5px 20px 0 0;
  &:last-child {
    margin-right: 0;
  }
  ${params?.isMobile && `
    width: 100%;
    border-bottom: 1px solid ${params?.textColor};
    padding: 0 0 15px 0;
    margin: 5px 0 15px 0;
    
    &:last-child {
      border: 0;
      margin-bottom: 0;
    }
  `}
`;

const checkbox = (params) => css`
  border: 0;
  outline: none;
  background: transparent;
  color: ${params?.textColor};
  display: flex;
  align-items: center;
  cursor: pointer;
  margin: 0 13px 0 8px;
`;

const list = (params) => css`
  box-sizing: border-box;
  margin-top: 10px;
  position: relative;
  z-index: 10;
  display: grid;
  grid-column: 1;
  grid-gap: 18px;
  overflow-x: hidden;

  ${params?.isMobile ? '' : (`
    max-height: 170px;
    overflow-y: auto;
    scrollbar-color: ${params?.handleColor} ${params?.trackColor};
    scrollbar-width: ${params?.weight};

    ::-webkit-scrollbar {
        width: ${webkitScrollBarWeight[params?.weight]};
        margin-right: 4px;
        right: 4px;
    }
    ::-webkit-scrollbar-track {
        background:  ${params?.trackColor};
        border-radius: 3px;
        box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 1);
    }
    ::-webkit-scrollbar-thumb {
        border-radius: 3px;
        background: ${params?.handleColor};
        ${params?.hoverHandleColor && (`
          &:hover {
            background: ${params?.hoverHandleColor};
          }
        `)}
    }
  `)}
  ${!params?.isListOpen && params?.isMobile && `
    display: none;
  `}
`;

const title = css`
  margin: 0 0 10px 0!important;
  font-weight: bold;
  font-size; 1em;
`;

const squareIcon = (params) => css`
  border: 2px solid ${params?.hoverBackgroundColor};
  border-radius: 3px;
  width: 14px;
  height: 14px;
  display: block;
  margin-right: 10px;
  ${params?.isActive && (
    `background: ${params?.hoverBackgroundColor};`
  )}
`;

const textBox = (params) => css`
  width: 140px;
  overflow: hidden;
  position: relative;
  text-align: left;
  height: 1.5em;
  line-height: 1.5em;

  ${!hasTransparency(params?.backgroundColor) && (`
    &:after {
      content: '';
      position: absolute;
      width: 8px;
      right: -2px;
      top: 0;
      bottom: 0;
      transition: opacity 2s linear;
      opacity: 1;
      display: block;
      background: rgb(${hexToRgb(params?.backgroundColor)});
      background: linear-gradient(
        90deg,
        rgba(${hexToRgb(params?.backgroundColor)},0) 0%,
        rgba(${hexToRgb(params?.backgroundColor)},1) 100%);
    }
  `)}

  & > span {
    position: absolute;
    white-space: nowrap;
    transform: translateX(0);
    transition: 1s;
    min-width: fill-available;
  }
  
  ${params?.isMobile ? (`
    &:active {
      &:after {
        opacity: 0;
        transition: visibility 0s 2s, opacity 2s linear
      }
      > span {
        transform: translateX(calc(140px - 100%));
      }
    }
  `) : (`
    &:hover {
      &:after {
        opacity: 0;
        transition: visibility 0s 2s, opacity 2s linear
      }
      > span {
        transform: translateX(calc(140px - 100%));
      }
    }
  `)}
`;

const headerWrapper = (params) => css`
  ${params?.isMobile && `
    display: flex;
    justify-content: space-between;
    width: 100%;
  `}
`;

const toggleBtn = (params) => css`
  background: transparent;
  color: ${params?.textColor};
  outline: 0;
  border: none;
`;

const CheckboxList = ({
  onChange,
  themeConfig: {
    multiDropdown,
    bubbles,
    scrollBars,
  },
  isMobile,
  filter: {
    options,
    label,
    activeValue,
    key: filterKey,
    hasFilterData,
  },
}) => {
  const [isAllChecked, setIsAllChecked] = useState(activeValue?.length === options?.length);
  const [isListOpen, setIsListOpen] = useState(false);

  useEffect(() => {
    setIsAllChecked(activeValue?.length === options?.length);
  }, [activeValue, options]);

  const getNextActiveValue = (id) => (e) => {
    if (id === 'all') {
      let nextActiveValue;
      if (!isAllChecked) {
        nextActiveValue = options.map((o) => o.id);
      } else {
        nextActiveValue = hasFilterData ? ['null'] : [];
      }
      onChange(filterKey, nextActiveValue)(e);
      setIsAllChecked(!isAllChecked);
    } else if (activeValue?.includes(id?.toString())) {
      onChange(filterKey, activeValue.filter((val) => val !== id.toString()))(e);
    } else {
      activeValue.push(id);
      onChange(filterKey, activeValue)(e);
    }
  };

  const handleListToggle = () => {
    setIsListOpen(!isListOpen);
  };

  // reorder options to have active on top
  const isActiveOption = (option) => activeValue?.includes(option?.id?.toString());
  const activeOptions = options.filter((o) => isActiveOption(o));
  const inactiveOptions = options.filter((o) => !isActiveOption(o));
  const sortedOptions = [...activeOptions, ...inactiveOptions];

  return (
    <div className={wrapper({ isMobile, ...multiDropdown })}>
      <div className={headerWrapper({ isMobile })}>
        <button
          type="button"
          className={cx(title, checkbox(multiDropdown))}
          onClick={getNextActiveValue('all')}
        >
          <i
            className={squareIcon({
              ...bubbles,
              isActive: isAllChecked,
            })}
          />
          <div className={textBox({ backgroundColor: multiDropdown?.backgroundColor })}><span>{label}</span></div>
        </button>
        {isMobile && (
          <button type="button" onClick={handleListToggle} className={toggleBtn(multiDropdown)}>
            {isListOpen ? <Minus /> : <Plus />}
          </button>
        )}
      </div>
      <div className={list({ isMobile, ...scrollBars, isListOpen })}>
        {sortedOptions
          .map((o) => (
            <button key={o.id} type="button" className={checkbox(multiDropdown)} onClick={getNextActiveValue(o?.id)}>
              <i className={squareIcon({ ...bubbles, isActive: activeValue?.includes(o?.id?.toString()) })} />
              <div className={textBox({ backgroundColor: multiDropdown?.backgroundColor })}>
                <span>{o.label || o.name}</span>
              </div>
            </button>
          ))}
      </div>
    </div>
  );
};

CheckboxList.propTypes = {
  onChange: PropTypes.func,
  filter: PropTypes.object,
  themeConfig: PropTypes.object,
  isMobile: PropTypes.bool,
};

export default CheckboxList;
